import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    TextField,
    Grid,
    LinearProgress
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { analysisService } from "../../../shared/services/api/analysis/analysisService";
import { useToast } from "../../../shared/hooks";
import { ToastContent } from "../../../shared/components";
import { format, subDays } from "date-fns";
import { globalMaskDocument } from "../../../shared/utils/globalMask-document";

interface IAnalysisErrorModalProps {
    open: boolean;
    onClose: () => void;
}

export const AnalysisErrorModal: React.FC<IAnalysisErrorModalProps> = ({ open, onClose }) => {
    const [analyses, setAnalyses] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [limit, setLimit] = useState(10);

    // Resend processing state
    const [isResending, setIsResending] = useState(false);
    const [processingStatus, setProcessingStatus] = useState("");
    const [currentProcessingId, setCurrentProcessingId] = useState<string | null>(null);
    const [processedIds, setProcessedIds] = useState<string[]>([]);
    const [resendSummary, setResendSummary] = useState<{ total: number; success: number; error: number } | null>(null);

    // Initial date state: Last 30 days
    const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

    const toastHandler = useToast();

    const fetchAnalyses = async (
        start = startDate,
        end = endDate,
        lim = limit
    ) => {
        setLoading(true);
        try {
            const result = await analysisService.getAnalysisError(start, end, "", lim);
            if (result instanceof Error) {
                toastHandler.present({
                    type: "error",
                    messageOrContent: <ToastContent title="Erro" content={result.message} />
                });
            } else {
                const data = result?.results || result?.data || (Array.isArray(result) ? result : []);
                setAnalyses(data);
            }
        } catch (error) {
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Effect for opening (Reset and Initial Fetch)
    useEffect(() => {
        if (open) {
            const defaultStart = format(subDays(new Date(), 30), 'yyyy-MM-dd');
            const defaultEnd = format(new Date(), 'yyyy-MM-dd');

            setStartDate(defaultStart);
            setEndDate(defaultEnd);
            setSelectedIds([]);
            setLimit(10);
            setProcessedIds([]);
            setIsResending(false);
            setProcessingStatus("");
            setCurrentProcessingId(null);
            setResendSummary(null);

            fetchAnalyses(defaultStart, defaultEnd, 10);
        }
    }, [open]);

    // Effect for pagination (Load More)
    useEffect(() => {
        if (open && limit > 10) {
            fetchAnalyses();
        }
    }, [limit]);

    const handleFilter = () => {
        setLimit(10);
        setProcessedIds([]);
        setSelectedIds([]);
        setResendSummary(null);
        fetchAnalyses(startDate, endDate, 10);
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelecteds = analyses
                .filter(n => !processedIds.includes(n.id))
                .map((n) => n.id);
            setSelectedIds(newSelecteds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
        if (isResending) return;

        const selectedIndex = selectedIds.indexOf(id);
        let newSelected: string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedIds, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedIds.slice(1));
        } else if (selectedIndex === selectedIds.length - 1) {
            newSelected = newSelected.concat(selectedIds.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedIds.slice(0, selectedIndex),
                selectedIds.slice(selectedIndex + 1),
            );
        }
        setSelectedIds(newSelected);
    };

    const handleResend = async () => {
        setIsResending(true);
        setProcessedIds([]);
        let successCount = 0;
        let errorCount = 0;
        const total = selectedIds.length;

        for (let i = 0; i < total; i++) {
            const id = selectedIds[i];
            const analysis = analyses.find(a => a.id === id);

            if (analysis) {
                setCurrentProcessingId(id);
                setProcessingStatus(`Reenviando ${i + 1} de ${total}...`);

                if (i > 0) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }

                const numero = analysis.numero || "1";

                try {
                    const result = await analysisService.resendAnalysis(id, numero);
                    if (result instanceof Error) {
                        errorCount++;
                    } else {
                        successCount++;
                    }
                } catch (e) {
                    errorCount++;
                }

                setProcessedIds(prev => [...prev, id]);
            }
        }

        setIsResending(false);
        setCurrentProcessingId(null);
        setProcessingStatus("");
        setResendSummary({
            total: total,
            success: successCount,
            error: errorCount
        });

        toastHandler.present({
            type: successCount > 0 ? "success" : "error",
            messageOrContent: <ToastContent
                title="Processamento Concluído"
                content={`Sucesso: ${successCount}, Erros: ${errorCount}`}
            />
        });

        setSelectedIds([]);
    };

    const handleLoadMore = () => {
        setLimit(prev => prev + 10);
    };

    return (
        <Dialog open={open} onClose={isResending ? undefined : onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                Análises com Erro de Birô {analyses.length > 0 && `(${analyses.length})`}
                {isResending && (
                    <Box mt={1}>
                        <Typography variant="body2" color="primary">
                            {processingStatus}
                        </Typography>
                        <LinearProgress />
                    </Box>
                )}
            </DialogTitle>
            <DialogContent>
                <Box mb={2} mt={1}>
                    {resendSummary && (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                mb: 2,
                                bgcolor: 'grey.100',
                                display: 'flex',
                                justifyContent: 'space-around',
                                alignItems: 'center'
                            }}
                        >
                            <Box textAlign="center">
                                <Typography variant="subtitle2" color="textSecondary">Processadas</Typography>
                                <Typography variant="h6">{resendSummary.total}</Typography>
                            </Box>
                            <Box textAlign="center">
                                <Typography variant="subtitle2" color="success.main">Sucesso</Typography>
                                <Typography variant="h6" color="success.main">{resendSummary.success}</Typography>
                            </Box>
                            <Box textAlign="center">
                                <Typography variant="subtitle2" color="error.main">Erros</Typography>
                                <Typography variant="h6" color="error.main">{resendSummary.error}</Typography>
                            </Box>
                        </Paper>
                    )}
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4} md={3}>
                            <TextField
                                label="Data Inicial"
                                type="date"
                                fullWidth
                                size="small"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                disabled={isResending}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <TextField
                                label="Data Final"
                                type="date"
                                fullWidth
                                size="small"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                disabled={isResending}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleFilter}
                                fullWidth
                                disabled={isResending}
                            >
                                Filtrar
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                {loading && analyses.length === 0 ? (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={selectedIds.length > 0 && selectedIds.length < analyses.length}
                                                checked={analyses.length > 0 && selectedIds.length === analyses.length}
                                                onChange={handleSelectAll}
                                                disabled={isResending}
                                            />
                                        </TableCell>
                                        <TableCell>Data</TableCell>
                                        <TableCell>Nome</TableCell>
                                        <TableCell>Documento</TableCell>
                                        <TableCell>Id Analise</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {analyses.map((row) => {
                                        const isItemSelected = selectedIds.indexOf(row.id) !== -1;
                                        const isProcessing = currentProcessingId === row.id;
                                        const isProcessed = processedIds.includes(row.id);

                                        return (
                                            <TableRow
                                                hover
                                                onClick={(event) => !isProcessed && !isResending && handleClick(event, row.id)}
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={row.id}
                                                selected={isItemSelected}
                                                sx={{
                                                    backgroundColor: isProcessing ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                                                    opacity: isProcessed ? 0.6 : 1
                                                }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isItemSelected}
                                                        disabled={isResending || isProcessed}
                                                    />
                                                </TableCell>
                                                <TableCell>{row.createdAt ? format(new Date(row.createdAt), 'dd/MM/yyyy HH:mm') : '-'}</TableCell>
                                                <TableCell sx={{ fontWeight: isProcessing ? 'bold' : 'normal' }}>
                                                    {row.name} {isProcessing && "(Processando...)"} {isProcessed && "(Concluído)"}
                                                </TableCell>
                                                <TableCell>{globalMaskDocument(row.document)}</TableCell>
                                                <TableCell>{row.id || row.id || "Erro desconhecido"}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Button onClick={handleLoadMore} disabled={loading || isResending}>
                                {loading ? "Carregando..." : "Carregar Mais"}
                            </Button>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" disabled={isResending}>
                    Fechar
                </Button>
                <Button
                    onClick={handleResend}
                    color="primary"
                    variant="contained"
                    disabled={selectedIds.length === 0 || loading || isResending}
                >
                    {isResending ? "Processando..." : `Reenviar Selecionados (${selectedIds.length})`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
